import { UndoOutlined, WalletOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, DatePicker, Input, InputNumber, Modal, message, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import type { AppRole } from "@/shared/config/navigation";
import { PaymentsNavigation } from "../components/PaymentsNavigation";
import {
	getCancelledSalePayments,
	getPaymentApiErrorMessage,
	refundPayment,
} from "../services/payments.api";
import type { CancelledSalePaymentRecord, PaymentMethod } from "../types/payments.types";
import { formatUzs, paymentMethodLabels } from "../utils/payments.utils";

const methodColors: Record<PaymentMethod, string> = {
	naqd: "green",
	bank: "blue",
	karta: "purple",
};

function remainingOf(payment: CancelledSalePaymentRecord) {
	return Math.max(0, payment.amountUzs - payment.refundedAmount);
}

export function CancelledSalePaymentsPage() {
	const navigate = useNavigate();
	const { role } = useOutletContext<{ role: AppRole }>();
	const isOrgAdmin = role === "admin";
	const queryClient = useQueryClient();
	const [refundingPayment, setRefundingPayment] = useState<CancelledSalePaymentRecord | null>(null);
	const [refundAmount, setRefundAmount] = useState(0);
	const [refundDate, setRefundDate] = useState(dayjs().format("YYYY-MM-DD"));
	const [refundReason, setRefundReason] = useState("");

	const paymentsQuery = useQuery({
		queryKey: ["payments", "cancelled-sales"],
		queryFn: getCancelledSalePayments,
	});

	const payments = paymentsQuery.data ?? [];

	const totals = useMemo(() => {
		return payments.reduce(
			(acc, payment) => {
				acc.pending += remainingOf(payment);
				acc.refunded += payment.refundedAmount;
				return acc;
			},
			{ pending: 0, refunded: 0 }
		);
	}, [payments]);

	const refundMutation = useMutation({
		mutationFn: ({
			id,
			amount,
			refundedAt,
			reason,
		}: {
			id: string;
			amount: number;
			refundedAt: string;
			reason?: string;
		}) => refundPayment(id, { amount, refundedAt, reason }),
		onSuccess: async () => {
			message.success("To'lov qaytarildi");
			setRefundingPayment(null);
			setRefundReason("");
			await queryClient.invalidateQueries({ queryKey: ["payments"] });
			await queryClient.invalidateQueries({ queryKey: ["sales"] });
			await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
		},
		onError: (error) => {
			message.error(getPaymentApiErrorMessage(error, "To'lovni qaytarishda xatolik yuz berdi"));
		},
	});

	const openRefundModal = (record: CancelledSalePaymentRecord) => {
		setRefundingPayment(record);
		setRefundAmount(remainingOf(record));
		setRefundDate(dayjs().format("YYYY-MM-DD"));
		setRefundReason("");
	};

	const columns: ColumnsType<CancelledSalePaymentRecord> = [
		{
			title: "Sana",
			dataIndex: "paymentDate",
			key: "paymentDate",
			width: 116,
		},
		{
			title: "Mijoz",
			key: "client",
			render: (_, record) => (
				<div>
					<strong>{record.clientName}</strong>
					<div className="crm-table-sub">Shartnoma #{record.saleNumber}</div>
				</div>
			),
		},
		{
			title: "Xona",
			key: "room",
			render: (_, record) => (
				<div>
					<strong>{record.roomNumber}-xona</strong>
					<span className="crm-table-sub">{record.buildingName}</span>
				</div>
			),
		},
		{
			title: "Bekor qilingan",
			key: "cancelled",
			render: (_, record) => (
				<div>
					<span>{record.cancelledAt ? record.cancelledAt.slice(0, 10) : "-"}</span>
					{record.cancelReason ? <div className="crm-table-sub">{record.cancelReason}</div> : null}
				</div>
			),
		},
		{
			title: "To'langan / Qaytarilgan",
			key: "amounts",
			align: "right",
			render: (_, record) => (
				<div className="crm-money-cell">
					<strong>{formatUzs(record.amountUzs)}</strong>
					{record.refundedAmount > 0 ? (
						<span>Qaytarilgan: {formatUzs(record.refundedAmount)}</span>
					) : null}
				</div>
			),
		},
		{
			title: "Usul",
			dataIndex: "paymentMethod",
			key: "paymentMethod",
			render: (value: PaymentMethod) => (
				<Tag color={methodColors[value]}>{paymentMethodLabels[value]}</Tag>
			),
		},
		{
			title: "Qabul qildi",
			dataIndex: "receivedBy",
			key: "receivedBy",
		},
		{
			title: "Holat",
			key: "status",
			render: (_, record) => {
				const remaining = remainingOf(record);
				const status =
					record.refundedAmount <= 0
						? { color: "orange", label: "Qaytarilmagan" }
						: remaining > 0
							? { color: "gold", label: "Qisman qaytarilgan" }
							: { color: "red", label: "To'liq qaytarilgan" };

				return (
					<div>
						<Tag color={status.color}>{status.label}</Tag>
						{record.refundedAt ? (
							<div className="crm-table-sub">{record.refundedAt.slice(0, 10)}</div>
						) : null}
						{record.refundReason ? (
							<div className="crm-table-sub">{record.refundReason}</div>
						) : null}
					</div>
				);
			},
		},
		{
			title: "",
			key: "actions",
			width: 140,
			render: (_, record) =>
				remainingOf(record) <= 0 || !isOrgAdmin ? null : (
					<Button
						icon={<UndoOutlined />}
						size="small"
						onClick={(event) => {
							event.stopPropagation();
							openRefundModal(record);
						}}
					>
						Qaytarish
					</Button>
				),
		},
	];

	const refundModalRemaining = refundingPayment ? remainingOf(refundingPayment) : 0;

	return (
		<div className="crm-payments">
			<div className="crm-page-head">
				<div>
					<h1>Bekor qilingan sotuvlar to'lovlari</h1>
					<p>
						Bekor qilingan shartnomalar bo'yicha qabul qilingan to'lovlar · {payments.length} yozuv
					</p>
				</div>
			</div>

			<PaymentsNavigation activeTab="cancelled" role={role} />

			<div className="crm-payment-kpis">
				<div className="crm-kpi">
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon amber">
							<WalletOutlined />
						</span>
						Qaytarilishi kerak
					</div>
					<div className="crm-kpi-value">
						<strong>{formatUzs(totals.pending)}</strong>
					</div>
				</div>
				<div className="crm-kpi">
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon red">
							<WalletOutlined />
						</span>
						Qaytarilgan
					</div>
					<div className="crm-kpi-value">
						<strong>{formatUzs(totals.refunded)}</strong>
					</div>
				</div>
			</div>

			<div className="crm-card crm-table-card">
				<Table
					columns={columns}
					dataSource={payments}
					loading={paymentsQuery.isLoading}
					locale={{ emptyText: "Bekor qilingan sotuvlar to'lovi topilmadi" }}
					rowKey="id"
					scroll={{ x: 1020 }}
					pagination={{ pageSize: 8 }}
					onRow={(record) => ({
						onClick: () =>
							navigate(`/sales/${record.saleId}`, { state: { returnTo: "/payments/cancelled" } }),
						style: { cursor: "pointer" },
					})}
				/>
			</div>

			<Modal
				open={refundingPayment !== null}
				onCancel={() => setRefundingPayment(null)}
				title="To'lovni qaytarish"
				okText="Qaytarish"
				cancelText="Bekor qilish"
				okButtonProps={{
					danger: true,
					disabled: refundAmount <= 0 || refundAmount > refundModalRemaining,
				}}
				confirmLoading={refundMutation.isPending}
				onOk={() => {
					if (!refundingPayment) {
						return;
					}
					refundMutation.mutate({
						id: refundingPayment.id,
						amount: refundAmount,
						refundedAt: refundDate,
						reason: refundReason.trim() || undefined,
					});
				}}
			>
				{refundingPayment ? (
					<div className="crm-payment-edit-body">
						<div className="crm-kv">
							<span>To'lov summasi</span>
							<b>{formatUzs(refundingPayment.amountUzs)}</b>
						</div>
						<div className="crm-kv">
							<span>Avval qaytarilgan</span>
							<b>{formatUzs(refundingPayment.refundedAmount)}</b>
						</div>
						<div className="crm-kv">
							<span>Qaytarish mumkin</span>
							<b>{formatUzs(refundModalRemaining)}</b>
						</div>
						<label className="crm-field" htmlFor="refund-amount">
							<span>Qaytarish summasi (UZS)</span>
							<InputNumber
								id="refund-amount"
								value={refundAmount}
								onChange={(value) => setRefundAmount(value ?? 0)}
								min={1}
								max={refundModalRemaining}
								style={{ width: "100%" }}
								formatter={(value) => `${value ?? ""}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
								parser={(value) => Number((value ?? "").replace(/\s/g, ""))}
							/>
						</label>
						<label className="crm-field" htmlFor="refund-date">
							<span>Qaytarish sanasi</span>
							<DatePicker
								id="refund-date"
								value={dayjs(refundDate)}
								onChange={(_, value) =>
									setRefundDate(Array.isArray(value) ? refundDate : value || refundDate)
								}
								style={{ width: "100%" }}
							/>
						</label>
						<label className="crm-field" htmlFor="refund-reason">
							<span>Izoh (ixtiyoriy)</span>
							<Input.TextArea
								id="refund-reason"
								value={refundReason}
								onChange={(event) => setRefundReason(event.target.value)}
								rows={2}
								autoSize={{ minRows: 2, maxRows: 3 }}
							/>
						</label>
					</div>
				) : null}
			</Modal>
		</div>
	);
}

export default CancelledSalePaymentsPage;
